import { useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { selectUnderlyingOptionsTradeData } from "../selectors/options"
import { getSecurityKey } from "../util/security"
import { selectExpandedRows, selectOptionChainTimeInterval, selectPrimarySecurity } from "../selectors/home"
import { fetchOptionChainV2 } from "../util/options"
import { useDispatch } from "react-redux"
import { selectOptionChainVisibleStrikes } from "../selectors/common"
import { GetOptionChainRequestV2 } from "../proto/generated/candle-data-depot-option-chain"
import useSelectPrimaryOrUnderlyingSecurity from "./useSelectPrimaryOrUnderlyingSecurity"
import { setOptionChainExpandedRows } from "../reducer/home.reducer"
import { isOptionChainLiveView, OptionChainViewKey } from "../assets/constants"
import { selectSimulatorDate } from "../selectors/simulator"
import { GetHistoricOptionChainRequest } from "../services/apis/simulator"
import { matchPath, useLocation, useParams } from "react-router-dom"
import ROUTES from "../assets/routes"
import { clearOptionChain } from "../reducer/options.reducer"
import { useUserOptional } from "../context/UserContext"
import { Security } from "../proto/generated/security"

interface Props {
    type?: 'extended' | 'normal'
    open?: boolean
    /** When set (e.g. widget dashboard), drives fetch + settings instead of route-based LIVE/HISTORICAL. */
    optionChainViewKey?: OptionChainViewKey
    /** Widget dashboard: use this security instead of global primary. */
    widgetSecurity?: Security
    /** Stable underlying key for widget dashboard fetches. */
    widgetSecurityKey?: string
}

/** Tracks last successful widget fetch per underlying key across mount cycles. */
const widgetFetchSignatureByKey = new Map<string, string>()
/** Tracks widget fetch settings (strikes + interval) per underlying key. */
const widgetFetchSettingsByKey = new Map<string, string>()
/** Tracks last successful live-page fetch per underlying key. */
const liveFetchStateByKey = new Map<string, string>()

const useOptionsTradeData = ({
    type = 'normal',
    open = true,
    optionChainViewKey: optionChainViewKeyProp,
    widgetSecurity,
    widgetSecurityKey = '',
}: Props) => {
    const location = useLocation()
    const routeViewKey = matchPath(ROUTES.simulator, location.pathname) ? OptionChainViewKey.HISTORICAL : OptionChainViewKey.LIVE
    const [routeOptionChainViewKey, setRouteOptionChainViewKey] = useState(routeViewKey)
    const optionChainViewKey = optionChainViewKeyProp ?? routeOptionChainViewKey
    const isWidgetView = optionChainViewKey === OptionChainViewKey.WIDGET
    const [loading, setLoading] = useState(!isWidgetView)
    const { key, primary } = useSelector(selectPrimarySecurity)
    const noOfVisibleStrikes = useSelector(selectOptionChainVisibleStrikes)
    const { primaryOrUnderlying: globalSelectedSecurity } = useSelectPrimaryOrUnderlyingSecurity()
    const selectedSecurity = widgetSecurity ?? globalSelectedSecurity
    const effectiveKey = useMemo(() => {
        if (widgetSecurityKey) return widgetSecurityKey
        if (selectedSecurity) return getSecurityKey(selectedSecurity.exchange, selectedSecurity.code)
        return key
    }, [widgetSecurityKey, selectedSecurity, key])
    const expandedRows = useSelector(selectExpandedRows(optionChainViewKey))
    const expandedRowKey = expandedRows.join(',')
    const optionsExpiries = useSelector(selectUnderlyingOptionsTradeData(effectiveKey))
    const timeInterval = useSelector(selectOptionChainTimeInterval(optionChainViewKey, type))
    const dispatch = useDispatch()
    const initialLoaded = useRef(false)
    const prevFetchState = useRef('{}')
    const prevSimulatorFetchState = useRef(0)
    const prevKey = useRef(effectiveKey)
    const referenceTimestamp = useSelector(selectSimulatorDate)
    const { exchange: urlExchange, symbol: urlCode } = useParams()
    const userContext = useUserOptional()
    const simulatorAccessible = userContext?.user?.simulatorAccessible ?? false

    useEffect(() => {
        return () => {
            if (!optionChainViewKeyProp && optionChainViewKey === OptionChainViewKey.HISTORICAL) {
                dispatch(clearOptionChain())
            }
        }
    }, [])

    useEffect(() => {
        if (optionChainViewKeyProp) return
        if (matchPath(ROUTES.simulator, location.pathname)) {
            setRouteOptionChainViewKey(OptionChainViewKey.HISTORICAL)
        } else {
            setRouteOptionChainViewKey(OptionChainViewKey.LIVE)
        }
    }, [location.pathname, optionChainViewKeyProp])

    useEffect(() => {
        if (isWidgetView || !open) return
        setLoading(true)
    }, [effectiveKey, open, isWidgetView])

    useEffect(() => {
        if (!loading || isWidgetView) return
        if (optionsExpiries !== undefined) {
            setLoading(false)
        }
    }, [loading, optionsExpiries, isWidgetView])

    useEffect(() => {
        if (effectiveKey === `${urlExchange}:${urlCode}` && open) {
            if (optionChainViewKey === OptionChainViewKey.HISTORICAL && simulatorAccessible) {
                if (selectedSecurity && primary) {
                    let expandedRowsPassed = expandedRows
                    if (prevKey.current !== effectiveKey) {
                        dispatch(setOptionChainExpandedRows({
                            expandedRows: ['0'],
                            optionChainViewKey: OptionChainViewKey.HISTORICAL
                        }))
                        expandedRowsPassed = ['0']
                    }

                    const securityForFetch = selectedSecurity ?? primary
                    const payload: GetHistoricOptionChainRequest = {
                        security: {
                            code: securityForFetch.code,
                            exchange: securityForFetch.exchange
                        },
                        referenceTimestamp
                    }

                    if (!initialLoaded.current || prevSimulatorFetchState.current !== payload.referenceTimestamp || prevKey.current !== effectiveKey) {
                        if (!!referenceTimestamp) {
                            fetchOptionChainV2(payload, dispatch, undefined, 'both', true, expandedRowsPassed, referenceTimestamp)
                            initialLoaded.current = true
                            prevSimulatorFetchState.current = referenceTimestamp
                            prevKey.current = effectiveKey
                        }
                    }
                }
            }
        }
    }, [selectedSecurity, primary, referenceTimestamp, optionChainViewKey, open, simulatorAccessible, effectiveKey, urlExchange, urlCode, expandedRows, dispatch])

    useEffect(() => {
        if (isWidgetView) return

        const onOptionChainPage = effectiveKey === `${urlExchange}:${urlCode}`
        const shouldFetch = open && (onOptionChainPage || type === 'extended')
        if (!shouldFetch) return

        const canFetch = selectedSecurity && primary && isOptionChainLiveView(optionChainViewKey)
        if (!canFetch) return

        let expiries: number[] = []
        let expandedRowsPassed = expandedRows
        if (prevKey.current !== effectiveKey) {
            dispatch(setOptionChainExpandedRows({
                expandedRows: ['0'],
                optionChainViewKey
            }))
            expandedRowsPassed = ['0']
            prevKey.current = effectiveKey
        }
        expandedRowsPassed.forEach(row => {
            if (optionsExpiries && (optionsExpiries[Number(row)]?.trades?.length !== Number(noOfVisibleStrikes))) {
                expiries.push(optionsExpiries[Number(row)].expiration.expiry?.expiry ?? 0)
            }
        })
        const prevTimeBucket = (JSON.parse(prevFetchState.current) as GetOptionChainRequestV2)?.timeBucket
        if (prevTimeBucket) {
            if ((prevTimeBucket.timeSeries !== timeInterval.timeSeries || prevTimeBucket.value !== timeInterval.value) && optionsExpiries) {
                expiries = []
                expandedRows.forEach(row => {
                    if (optionsExpiries) expiries.push(optionsExpiries[Number(row)].expiration.expiry?.expiry ?? 0)
                })
            }
        }
        const securityForFetch = selectedSecurity ?? primary
        const payload: GetOptionChainRequestV2 = {
            security: {
                code: securityForFetch.code,
                exchange: securityForFetch.exchange
            },
            expiries: expiries,
            noOfStrikes: noOfVisibleStrikes === 'all' ? -1 : Number(noOfVisibleStrikes),
            requestedFields: [],
            timeBucket: timeInterval,
            sendFutures: true
        }

        const payloadKey = JSON.stringify(payload)
        const hasCachedData = optionsExpiries !== undefined && liveFetchStateByKey.get(effectiveKey) === payloadKey

        if (expiries.length !== 0 || !hasCachedData) {
            fetchOptionChainV2(payload, dispatch, optionsExpiries, 'both', true, expandedRowsPassed)
            liveFetchStateByKey.set(effectiveKey, payloadKey)
        }
        initialLoaded.current = true
        prevFetchState.current = payloadKey
    }, [
        selectedSecurity,
        noOfVisibleStrikes,
        timeInterval,
        primary,
        optionChainViewKey,
        open,
        effectiveKey,
        urlExchange,
        urlCode,
        type,
        isWidgetView,
        expandedRowKey,
        dispatch,
    ])

    useEffect(() => {
        if (!isWidgetView || !widgetSecurity || !effectiveKey) return
        if (!isOptionChainLiveView(optionChainViewKey)) return

        const rowIndex = expandedRows[0] ?? '0'
        const fetchSignature = `${effectiveKey}:${rowIndex}:${noOfVisibleStrikes}:${timeInterval.timeSeries}:${timeInterval.value}`
        const settingsSignature = `${noOfVisibleStrikes}:${timeInterval.timeSeries}:${timeInterval.value}`

        const existingRow = optionsExpiries?.[Number(rowIndex)]
        const settingsUnchanged = widgetFetchSettingsByKey.get(effectiveKey) === settingsSignature
        const fetchSignatureMatches = widgetFetchSignatureByKey.get(effectiveKey) === fetchSignature

        if (optionsExpiries !== undefined && settingsUnchanged && fetchSignatureMatches) {
            return
        }

        const payload: GetOptionChainRequestV2 = {
            security: {
                code: widgetSecurity.code,
                exchange: widgetSecurity.exchange,
            },
            expiries: [],
            noOfStrikes: noOfVisibleStrikes === 'all' ? -1 : Number(noOfVisibleStrikes),
            requestedFields: [],
            timeBucket: timeInterval,
            sendFutures: true,
        }

        const expiryTs = existingRow?.expiration?.expiry?.expiry
        const hasRowData = (existingRow?.trades?.length ?? 0) > 0 && !existingRow?.loading
        if (expiryTs != null && !optionsExpiries?.length) {
            payload.expiries = []
        } else if (expiryTs != null && !hasRowData) {
            payload.expiries = [expiryTs]
        }

        fetchOptionChainV2(payload, dispatch, optionsExpiries, 'both', false, [rowIndex])
        widgetFetchSignatureByKey.set(effectiveKey, fetchSignature)
        widgetFetchSettingsByKey.set(effectiveKey, settingsSignature)
    }, [
        isWidgetView,
        widgetSecurity,
        effectiveKey,
        optionChainViewKey,
        noOfVisibleStrikes,
        timeInterval,
        expandedRowKey,
        dispatch,
    ])

    return { optionsTradesData: optionsExpiries, loading, key: effectiveKey }

}

export default useOptionsTradeData
